import React from 'react';
import _ from 'lodash';

interface SeparatorProps {
  turns: number;
  style: React.CSSProperties | undefined;
}

interface RadialSeparatorsProps {
  count: number;
  value: number;
  style: React.CSSProperties;
  greyStyle: React.CSSProperties;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const hexWithoutHash = hex.startsWith('#') ? hex.slice(1) : hex;
  const bigint = parseInt(hexWithoutHash, 16);
  return {
    r: Math.floor(bigint / (256 * 256)),
    g: Math.floor((bigint % (256 * 256)) / 256),
    b: bigint % 256,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map(x => x.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

function interpolateColor(
  startColor: { r: number; g: number; b: number },
  endColor: { r: number; g: number; b: number },
  factor: number,
): string {
  const result = {
    r: Math.round(startColor.r + factor * (endColor.r - startColor.r)),
    g: Math.round(startColor.g + factor * (endColor.g - startColor.g)),
    b: Math.round(startColor.b + factor * (endColor.b - startColor.b)),
  };
  return rgbToHex(result.r, result.g, result.b);
}

const Separator: React.FC<SeparatorProps> = ({ turns, style }) => (
  <div
    style={{
      position: 'absolute',
      height: '100%',
      transform: `rotate(${turns}turn)`,
    }}>
    <div style={style} />
  </div>
);

const RadialSeparators: React.FC<RadialSeparatorsProps> = ({ count, value, style, greyStyle }) => {
  const turns = 1 / count;
  const threshold = (value / 100) * count;
  const startColor = hexToRgb('#2F3A8B');
  const middleColor = hexToRgb('#36A9E1');
  const endColor = hexToRgb('#66C5DB');

  const middlePoint = count / 2;

  return (
    <>
      {_.range(count).map(index => {
        let interpolatedColor;
        if (index < middlePoint) {
          const factor = index / middlePoint;
          interpolatedColor = interpolateColor(startColor, middleColor, factor);
        } else if (index < threshold) {
          const factor = (index - middlePoint) / (threshold - middlePoint);
          interpolatedColor = interpolateColor(middleColor, endColor, factor);
        } else {
          interpolatedColor = greyStyle.background as string;
        }

        const currentStyle = index < threshold ? { ...style, background: interpolatedColor } : greyStyle;
        return <Separator key={index} turns={index * turns} style={currentStyle} />;
      })}
    </>
  );
};

export default RadialSeparators;
